import * as Accordion from "#root/components/accordion";
import { Case, Switch } from "#root/components/conditionals";
import * as Fields from "#root/components/form/fields";
import { GlyphLoading } from "#root/components/glyphs";
import Sidebar from "#root/components/sidebar";
import StandardEditor, { type StandardEditorProps } from "#root/components/standard-editor";
import { WithSidebar } from "#root/components/withSidebar";
import { articleCollection, type ArticleDetailSchema, type ArticleListItem, type ArticleListSchema } from "#root/schema/article";
import { useData } from "#root/utility/hooks";
import { mergeUpdate } from "#root/utility/merge-update";
import type { RecordModificationResponse } from "#root/utility/remote-data";
import { flatToNamedTree } from "#root/utility/tree";
import type { ReactNode } from "react";
import { useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import PageLayout from "./layout";

export default function ({ id }: { id: string }): ReactNode {
  const navigate = useNavigate();
  const [articles, setArticles] = useData(async () => {
    const { rows } = await articleCollection.list();
    return rows;
  }, []);

  const handleUpdate = useCallback(
    (data: RecordModificationResponse<ArticleListItem>) => {
      setArticles(mergeUpdate(articles!, data));

      if ("deleted" in data) {
        navigate("/admin/article");
      } else if ("created" in data) {
        navigate(`/admin/article/${data.created}`);
      }
    },
    [navigate, setArticles, articles],
  );

  if (!articles) {
    return (
      <PageLayout title="Article">
        <section>
          <GlyphLoading/>
        </section>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Admin - Article">
      <WithSidebar>
        <ArticleSidebar articles={articles} id={id}/>
        <section>
          <Switch>
            <Case expr={id === "new"}>
              <h1>New Article</h1>
              <ArticleEditor collection={articleCollection} onSave={handleUpdate}/>
            </Case>
            <Case expr={id != null}>
              <h1>Edit Article</h1>
              <ArticleEditor id={id} collection={articleCollection} onSave={handleUpdate} onDelete={handleUpdate}/>
            </Case>
          </Switch>
        </section>
      </WithSidebar>
    </PageLayout>
  );
}

function ArticleSidebar({ articles, id }: { articles: ArticleListItem[]; id: string }) {
  const navigate = useNavigate();
  const categories = useMemo(() => flatToNamedTree(articles, a => a.category, a => a.name), [articles]);

  return (
    <Sidebar>
      <button className="flat center" type="button" onClick={() => navigate("/admin/article/new")}>
        New Article
      </button>
      <Accordion.Parent initialItemId={id}>
        {categories.map(({ name, items }) => (
          <Accordion.Group key={name} label={name}>
            {items.map(item => (
              <Accordion.Item key={item.id} itemId={item.id}>
                <Link to={"/admin/article/" + item.id}>{item.name}</Link>
              </Accordion.Item>
            ))}
          </Accordion.Group>
        ))}
      </Accordion.Parent>
    </Sidebar>
  );
}

function ArticleEditor(props: StandardEditorProps<typeof ArticleListSchema, typeof ArticleDetailSchema>) {
  return (
    <StandardEditor {...props}>
      <Fields.Set name="name">
        <label>Article Name</label>
        <Fields.String/>
        <Fields.Validity/>
      </Fields.Set>
      <Fields.Set name="slug">
        <label>Slug String</label>
        <Fields.String/>
        <Fields.Validity/>
      </Fields.Set>
      <Fields.Set name="category">
        <label>Category</label>
        <Fields.CategoryName/>
        <Fields.Validity/>
      </Fields.Set>
      <Fields.Set name="page">
        <label>Article Page</label>
        <Fields.TextArea rows={10}/>
        <Fields.Validity/>
      </Fields.Set>
    </StandardEditor>
  );
}
